/**
 * VendorHub (ADR-0024): what a vendor touches, what evidence that calls for, how often it's
 * reviewed, and the risk rating worked out from those rules. Shared by the API and the browser,
 * so every color on screen traces to a rule here or in Settings > VendorHub > Rules.
 */

export const DATA_TYPES = ["phi", "pii", "payment", "cui"] as const;
export type DataType = (typeof DATA_TYPES)[number];
export const DATA_LABEL: Record<DataType, string> = { phi: "PHI", pii: "PII", payment: "Payment", cui: "CUI" };
export const DATA_HELP: Record<DataType, string> = {
  phi: "Protected health information (HIPAA)", pii: "Personal information: names with SSNs, account numbers, dates of birth",
  payment: "Card or bank payment data (PCI)", cui: "Controlled unclassified information (CMMC)",
};

export const CRITICALITY = ["high", "medium", "low"] as const;
export type Criticality = (typeof CRITICALITY)[number];
export const CRIT_LABEL: Record<Criticality, string> = { high: "High", medium: "Medium", low: "Low" };

export const EVIDENCE_KINDS = ["soc2_type2", "soc2_type1", "soc1", "bridge", "iso27001", "pci_aoc", "fedramp", "questionnaire", "pentest", "insurance", "baa_template", "other"] as const;
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number];
export const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  soc2_type2: "SOC 2 Type II report", soc2_type1: "SOC 2 Type I report", soc1: "SOC 1 report", bridge: "Bridge letter", iso27001: "ISO 27001 certificate",
  pci_aoc: "PCI attestation of compliance (AOC)", fedramp: "FedRAMP authorization", questionnaire: "Security questionnaire", pentest: "Penetration test summary",
  insurance: "Cyber insurance certificate", baa_template: "Business associate agreement (their template)", other: "Other",
};
/** How each kind is dated: a report has a period end, a certificate an expiry, a questionnaire the day it was answered. */
export const EVIDENCE_DATE: Record<EvidenceKind, "period_end" | "expires" | "answered" | "none"> = {
  soc2_type2: "period_end", soc2_type1: "period_end", soc1: "period_end", bridge: "expires", iso27001: "expires", pci_aoc: "expires", fedramp: "expires",
  questionnaire: "answered", pentest: "answered", insurance: "expires", baa_template: "none", other: "expires",
};
export const DATE_WORD = { period_end: "Period ends", expires: "Expires", answered: "Answered", none: "" } as const;

/** One rule: a vendor touching this data needs one of these kinds, no older than `months`. */
export interface Requirement { data: DataType; kinds: EvidenceKind[]; months: number }
export interface VhRules { requirements: Requirement[]; review_months: Record<Criticality, number>; warn_days: number }

export const DEFAULT_RULES: VhRules = {
  requirements: [
    { data: "phi", kinds: ["soc2_type2", "soc2_type1", "iso27001", "questionnaire"], months: 12 },
    { data: "payment", kinds: ["pci_aoc"], months: 12 },
    { data: "pii", kinds: ["soc2_type2", "soc2_type1", "iso27001", "questionnaire"], months: 24 },
    { data: "cui", kinds: ["fedramp"], months: 12 },
  ],
  review_months: { high: 12, medium: 24, low: 36 },
  warn_days: 60,
};

/** Merge saved rules over the defaults and check them; throws a readable message. */
export function cleanRules(raw: unknown): VhRules {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<VhRules>;
  const out: VhRules = { requirements: [], review_months: { ...DEFAULT_RULES.review_months }, warn_days: DEFAULT_RULES.warn_days };
  const reqs = Array.isArray(r.requirements) ? r.requirements : DEFAULT_RULES.requirements;
  for (const q of reqs) {
    if (!q || !(DATA_TYPES as readonly string[]).includes(q.data)) throw new Error("Each rule needs a data type.");
    const kinds = [...new Set((q.kinds ?? []).filter((k) => (EVIDENCE_KINDS as readonly string[]).includes(k)))] as EvidenceKind[];
    if (!kinds.length) throw new Error(`Pick at least one kind of evidence for ${DATA_LABEL[q.data]}.`);
    const months = Math.round(Number(q.months));
    if (!(months >= 1 && months <= 60)) throw new Error(`Months for ${DATA_LABEL[q.data]} must be 1 to 60.`);
    if (out.requirements.some((x) => x.data === q.data)) throw new Error(`${DATA_LABEL[q.data]} has two rules; keep one.`);
    out.requirements.push({ data: q.data, kinds, months });
  }
  for (const c of CRITICALITY) {
    const v = r.review_months?.[c];
    if (v === undefined) continue;
    const n = Math.round(Number(v));
    if (!(n >= 1 && n <= 60)) throw new Error(`Review every 1 to 60 months (${CRIT_LABEL[c]}).`);
    out.review_months[c] = n;
  }
  if (r.warn_days !== undefined) {
    const n = Math.round(Number(r.warn_days));
    if (!(n >= 7 && n <= 180)) throw new Error("Warn 7 to 180 days before evidence runs out.");
    out.warn_days = n;
  }
  return out;
}

// ---------------------------------------------------------------- dates
const addMonths = (iso: string, m: number) => {
  const [y, mo, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, mo - 1 + m, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(d, last));
  return t.toISOString().slice(0, 10);
};
const days = (a: string, b: string) => Math.round((Date.parse(b.slice(0, 10)) - Date.parse(a.slice(0, 10))) / 86400000);
export { addMonths as vhAddMonths };

export interface EvidenceItem { id: string; kind: EvidenceKind; dated_on: string | null; uploaded_at: string; file_name?: string | null; title?: string | null }

/**
 * The day an evidence item stops counting for a rule. A certificate counts until it expires; a
 * report or questionnaire counts for the rule's months from its period end or answer date
 * (upload date when there's no date).
 */
export function goodUntil(e: EvidenceItem, months: number): string | null {
  const how = EVIDENCE_DATE[e.kind];
  if (how === "none") return null;
  if (how === "expires") return e.dated_on ?? addMonths(e.uploaded_at, months);
  return addMonths(e.dated_on ?? e.uploaded_at, months);
}

export type ReqState = "met" | "expiring" | "expired" | "missing";
export interface ReqCheck { data: DataType; kinds: EvidenceKind[]; months: number; state: ReqState; until: string | null; evidence_id: string | null; text: string }

/** Each rule that applies to this vendor, and whether its evidence is in place. */
export function checkRequirements(data: DataType[], evidence: EvidenceItem[], rules: VhRules, today: string): ReqCheck[] {
  const out: ReqCheck[] = [];
  for (const q of rules.requirements) {
    if (!data.includes(q.data)) continue;
    let best: { e: EvidenceItem; until: string } | null = null;
    for (const e of evidence) {
      if (!q.kinds.includes(e.kind)) continue;
      const until = goodUntil(e, q.months);
      if (until && (!best || until > best.until)) best = { e, until };
    }
    const short = q.kinds.map((k) => EVIDENCE_LABEL[k].replace(/ report$| certificate$/, ""));
    const names = short.length > 1 ? `one of ${short.slice(0, -1).join(", ")} or ${short[short.length - 1]}` : short[0];
    if (!best) { out.push({ ...q, state: "missing", until: null, evidence_id: null, text: `${DATA_LABEL[q.data]}: needs ${names}; nothing on file` }); continue; }
    const left = days(today, best.until);
    const label = EVIDENCE_LABEL[best.e.kind];
    const state: ReqState = left < 0 ? "expired" : left <= rules.warn_days ? "expiring" : "met";
    const text = state === "expired" ? `${label} ran out ${fmtD(best.until)}` : state === "expiring" ? `${label} runs out in ${left} day${left === 1 ? "" : "s"}` : `${label} current to ${fmtD(best.until)}`;
    out.push({ ...q, state, until: best.until, evidence_id: best.e.id, text });
  }
  return out;
}

export interface ReviewState { months: number; next_on: string; state: "ok" | "due_soon" | "overdue" | "never"; text: string }
export function reviewState(criticality: Criticality, lastReviewed: string | null, createdAt: string, rules: VhRules, today: string): ReviewState {
  const months = rules.review_months[criticality];
  if (!lastReviewed) {
    // A new vendor gets its first review within 30 days of being added.
    const next = addMonths(createdAt, 1);
    return { months, next_on: next, state: days(today, next) < 0 ? "overdue" : "never", text: `Not reviewed yet; first review by ${fmtD(next)}` };
  }
  const next = addMonths(lastReviewed, months);
  const left = days(today, next);
  return { months, next_on: next, state: left < 0 ? "overdue" : left <= 30 ? "due_soon" : "ok",
    text: left < 0 ? `Review overdue since ${fmtD(next)}` : `Every ${months} months · next by ${fmtD(next)}` };
}

export type Risk = "high" | "medium" | "low";
export const RISK_LABEL: Record<Risk, string> = { high: "High", medium: "Medium", low: "Low" };
export interface Finding { severity: "high" | "medium" | "low"; status: "open" | "closed" }
export interface RiskResult { risk: Risk; reasons: string[] }

/**
 * Risk is worked out, never typed (ADR-0024 section 4):
 *   High    evidence for sensitive data missing or run out; an open high finding; a high-criticality vendor's review overdue
 *   Medium  evidence running out soon; any open finding; a review overdue; MFA not enforced on a vendor touching sensitive data
 *   Low     everything above in order
 */
export function riskOf(v: { criticality: Criticality; data: DataType[]; mfa: string }, reqs: ReqCheck[], review: ReviewState, findings: Finding[]): RiskResult {
  const high: string[] = [], med: string[] = [];
  for (const r of reqs) {
    if (r.state === "missing" || r.state === "expired") high.push(r.text);
    else if (r.state === "expiring") med.push(r.text);
  }
  const open = findings.filter((f) => f.status === "open");
  const openHigh = open.filter((f) => f.severity === "high").length;
  if (openHigh) high.push(`${openHigh} open high finding${openHigh === 1 ? "" : "s"}`);
  const openOther = open.length - openHigh;
  if (openOther) med.push(`${openOther} open finding${openOther === 1 ? "" : "s"}`);
  if (review.state === "overdue") (v.criticality === "high" ? high : med).push(review.text);
  if (v.data.length && v.mfa === "not_enforced") med.push("MFA isn't enforced for users");
  if (v.data.length && v.mfa === "none") high.push("No MFA for users on a vendor touching sensitive data");
  if (high.length) return { risk: "high", reasons: [...high, ...med] };
  if (med.length) return { risk: "medium", reasons: med };
  const why = v.data.length ? "Evidence current, no open findings, review on time" : "Touches no sensitive data; review on time";
  return { risk: "low", reasons: [why] };
}

export const MFA_OPTIONS: [string, string][] = [["enforced", "Enforced"], ["not_enforced", "Available, not enforced"], ["none", "Not available"], ["sso", "Through our single sign-on"], ["unknown", "Don't know yet"]];
export const CONTRACT_HOLDER: [string, string][] = [["us", "We hold it"], ["clients", "Each client, directly"], ["none", "No contract (free or click-through)"]];

export function fmtD(iso: string) {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${d}, ${y}`;
}
